// Shared Seller Insights ranking logic. Extracted verbatim from SellerInsights so the
// dedicated Seller Revenue Ranking page produces IDENTICAL numbers. No calculation changes.
import { buildItemCodeMap, isFreebieLine } from "@/lib/freebieCalc";

// Filter invoiced quotations by the Seller Insights filters (date/seller/city/state/brand/sku).
export function filterQuotations(quotations, sellerMap, filters) {
  const { dateFrom, dateTo, selectedSellers = [], selectedCities = [], selectedStates = [], selectedBrands = [], selectedSkus = [] } = filters;
  let filtered = (quotations || []).filter(q => !!q.invoice_date);
  if (dateFrom) filtered = filtered.filter(q => q.invoice_date?.substring(0, 10) >= dateFrom);
  if (dateTo) filtered = filtered.filter(q => q.invoice_date?.substring(0, 10) <= dateTo);
  if (selectedSellers.length > 0) filtered = filtered.filter(q => selectedSellers.includes(q.seller_id));
  if (selectedCities.length > 0) filtered = filtered.filter(q => {
    const s = sellerMap[q.seller_id];
    return s && selectedCities.includes(s.city);
  });
  if (selectedStates.length > 0) filtered = filtered.filter(q => {
    const s = sellerMap[q.seller_id];
    return s && selectedStates.includes(s.state);
  });
  if (selectedBrands.length > 0) filtered = filtered.filter(q => {
    const all = [...(q.items || []), ...(q.freebies || [])];
    return all.some(item => selectedBrands.includes(item.brand));
  });
  if (selectedSkus.length > 0) filtered = filtered.filter(q => {
    const all = [...(q.items || []), ...(q.freebies || [])];
    return all.some(item => selectedSkus.includes(item.sku_id));
  });
  return filtered;
}

// Aggregate per-seller stats (grouped by seller name), revenue-sorted desc.
export function computeSellerStats(filteredQuotations, sellerMap, products, filters) {
  const { selectedBrands = [], selectedSkus = [] } = filters;
  const itemCodeMap = buildItemCodeMap(products);
  const stats = {};

  (filteredQuotations || []).forEach(q => {
    const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
    const seller = sellerMap[q.seller_id] || {};
    if (!stats[name]) {
      stats[name] = {
        id: q.seller_id,
        name,
        city: seller.city || "-",
        state: seller.state || "-",
        totalRevenue: 0,
        totalMrp: 0,
        quotationCount: 0,
        totalUnits: 0,
        freebieValue: 0,
        allLinesMrpQty: 0,
        effectiveDiscounts: [],
      };
    }
    let items = q.items || [];
    if (selectedBrands.length > 0) items = items.filter(item => selectedBrands.includes(item.brand));
    if (selectedSkus.length > 0) items = items.filter(item => selectedSkus.includes(item.sku_id));

    const itemRevenue = selectedBrands.length > 0 || selectedSkus.length > 0
      ? items.reduce((sum, item) => sum + (item.invoice_value || 0), 0)
      : (q.total_invoice_value || 0);

    stats[name].totalRevenue += itemRevenue;
    stats[name].totalMrp += q.total_mrp_value || 0;
    stats[name].quotationCount += 1;
    stats[name].totalUnits += items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

    const allLines = [...(q.items || []), ...(q.freebies || [])];
    for (const ln of allLines) {
      const val = (Number(ln.mrp) || 0) * (Number(ln.quantity) || 0);
      stats[name].allLinesMrpQty += val;
      if (isFreebieLine(ln, itemCodeMap)) stats[name].freebieValue += val;
    }
    if (q.effective_discount != null) stats[name].effectiveDiscounts.push(q.effective_discount);
  });

  return Object.values(stats)
    .map(s => ({
      ...s,
      avgEffectiveDiscount: s.effectiveDiscounts.length > 0
        ? s.effectiveDiscounts.reduce((a, b) => a + b, 0) / s.effectiveDiscounts.length
        : 0,
      overallDiscount: s.totalMrp > 0 ? ((s.totalMrp - s.totalRevenue) / s.totalMrp) * 100 : 0,
      freebiePercent: s.allLinesMrpQty > 0 ? (s.freebieValue / s.allLinesMrpQty) * 100 : 0,
    }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue);
}
